// ══════════════════════════════════════════════════════════════
// KLASSDOC — Sentry frontend integration (Sprint 11.3)
//
// Env-var-gated. Active only when VITE_SENTRY_DSN_FRONTEND is set at
// build time (Vercel env var, "Production" scope). Without it, the
// init call no-ops and the bundle is unaffected (the SDK still gets
// imported but doesn't open the network connection).
//
// Source maps are uploaded at BUILD time by @sentry/vite-plugin in
// vite.config.js — that step needs SENTRY_AUTH_TOKEN + SENTRY_ORG +
// SENTRY_PROJECT (also Vercel build env, distinct from the runtime DSN).
// ══════════════════════════════════════════════════════════════

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN_FRONTEND;

export function initSentry() {
  if (!DSN) {
    // Silent on dev / previews where the DSN isn't configured. Logging
    // here would just produce noise on every local `npm run dev` boot.
    return;
  }
  try {
    Sentry.init({
      dsn: DSN,
      environment: import.meta.env.MODE || 'production',
      release: import.meta.env.VITE_RELEASE || 'unknown',
      // 10% performance sampling matches the backend default.
      tracesSampleRate: 0.1,
      // Session replay: off by default (privacy + bundle size), but
      // record the few seconds before an error fires so the engineer
      // sees what the user was clicking when it broke.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,    // never record user input text
          blockAllMedia: true,  // never record images / file previews
        }),
      ],
      // Don't send PII (cookies, IPs are still captured by Sentry's
      // server side but the SDK won't include extra PII headers).
      sendDefaultPii: false,
      ignoreErrors: [
        // React's hydration warnings are not real bugs in our SPA
        // (we don't use SSR), but the React 18 noise generator catches
        // them as errors sometimes.
        /^ResizeObserver loop/,
        // Browser extensions and ad blockers — not our code.
        'top.GLOBALS',
        /^chrome-extension:/,
        /^safari-extension:/,
        // Cancelled fetches from React StrictMode double-effect.
        'AbortError',
      ],
      beforeSend(event) {
        // Strip Authorization headers if any leak into breadcrumbs
        if (event.request?.headers) {
          delete event.request.headers.authorization;
          delete event.request.headers.cookie;
        }
        return event;
      },
    });
    console.log('[Sentry] initialized');
  } catch (e) {
    // Never let Sentry init crash the app.
    console.error('[Sentry] init failed (non-fatal):', e?.message || e);
  }
}

// Re-export for direct use in error boundaries / explicit captures
export { Sentry };
